/**
 * Format a duration in the format `HH:MM:SS`.
 *
 * @param duration - The duration in seconds
 *
 * @returns A string representing the duration
 */
export function formatDuration(duration: number) {
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = duration % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}

/**
 * Format a duration in human-readable relative format.
 * Outputs strings like "450ms", "5m 30s", "2h 15m", "3d 5h", "70+ days".
 * For durations over 30 days, the "+" indicates hours/minutes are truncated
 * (e.g., "70+ days" means 70 days and some additional hours).
 *
 * @param seconds - The duration in seconds
 * @returns A human-readable string representing the duration
 */
export function formatDurationHuman(seconds: number): string {
  if (seconds < 0) {
    return '0ms';
  }

  // Less than 1 second - show milliseconds
  if (seconds < 1) {
    const ms = Math.round(seconds * 1000);
    return `${ms}ms`;
  }

  // Less than 1 minute - show seconds
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }

  // Less than 1 hour - show minutes and seconds
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }

  // Less than 1 day - show hours and minutes
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }

  // Days
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);

  if (days > 30) {
    return `${days}+ days`;
  }

  return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
}

export default null; // silence a false warning
